"use client";

import dynamic from "next/dynamic";
import { useMemo } from "react";
import { rocketPlacementsForDisplay } from "@/lib/game/rocket";
import { GameIconButton } from "../game-ui";
import { useGame } from "../GameProvider";

const PixiFanfare = dynamic(
  () => import("./PixiFanfare").then((m) => m.PixiFanfare),
  { ssr: false },
);

const PixiRocketBadge = dynamic(
  () => import("./PixiRocketBadge").then((m) => m.PixiRocketBadge),
  { ssr: false },
);

export interface ActivitySuccessOverlayProps {
  show: boolean;
  title: string;
  subtitle?: string;
  rewardLabel?: string;
  playAgainLabel?: string;
  onPlayAgain: () => void;
  theme?: "amber" | "sky" | "slate" | "indigo";
}

const THEME_STYLES = {
  amber: {
    title: "text-amber-950 drop-shadow-[0_1px_0_rgba(255,255,255,0.9)]",
    subtitle: "text-amber-900 drop-shadow-[0_1px_0_rgba(255,255,255,0.75)]",
    reward: "text-amber-800 drop-shadow-[0_1px_0_rgba(255,255,255,0.7)]",
    retryTone: "amber" as const,
    menuTone: "slate" as const,
  },
  sky: {
    title: "text-sky-950 drop-shadow-[0_1px_0_rgba(255,255,255,0.9)]",
    subtitle: "text-sky-900 drop-shadow-[0_1px_0_rgba(255,255,255,0.75)]",
    reward: "text-sky-800 drop-shadow-[0_1px_0_rgba(255,255,255,0.7)]",
    retryTone: "sky" as const,
    menuTone: "slate" as const,
  },
  slate: {
    title: "text-white drop-shadow-[0_2px_10px_rgba(0,0,0,0.9)]",
    subtitle: "text-slate-100 drop-shadow-[0_2px_8px_rgba(0,0,0,0.85)]",
    reward: "text-green-200 drop-shadow-[0_2px_8px_rgba(0,0,0,0.85)]",
    retryTone: "sky" as const,
    menuTone: "slate" as const,
  },
  indigo: {
    title: "text-white drop-shadow-[0_2px_10px_rgba(0,0,0,0.9)]",
    subtitle: "text-violet-100 drop-shadow-[0_2px_8px_rgba(0,0,0,0.85)]",
    reward: "text-green-200 drop-shadow-[0_2px_8px_rgba(0,0,0,0.85)]",
    retryTone: "violet" as const,
    menuTone: "slate" as const,
  },
} as const;

export function ActivitySuccessOverlay({
  show,
  title,
  subtitle,
  rewardLabel,
  playAgainLabel = "Play again",
  onPlayAgain,
  theme = "sky",
}: ActivitySuccessOverlayProps) {
  const { goTo, progress } = useGame();
  const styles = THEME_STYLES[theme];
  const placements = useMemo(
    () => rocketPlacementsForDisplay(progress.builtRocket),
    [progress.builtRocket],
  );

  if (!show) return null;

  return (
    <div className="pointer-events-none absolute inset-0 z-20 flex items-end justify-center p-4 pb-[max(1rem,env(safe-area-inset-bottom))] sm:items-center">
      <PixiFanfare active={show} />

      <div
        className="pointer-events-auto relative z-30 flex w-full max-w-sm animate-success-pop flex-col items-center gap-4"
        role="dialog"
        aria-live="polite"
        aria-label="Activity complete"
      >
        <div className="h-28 w-28 sm:h-32 sm:w-32">
          <PixiRocketBadge placements={placements} size={112} className="h-full w-full" />
        </div>

        <div className="w-full space-y-1 text-center">
          <h2 className={`text-3xl font-extrabold tracking-tight sm:text-4xl ${styles.title}`}>
            {title}
          </h2>
          {subtitle && (
            <p className={`text-sm font-semibold sm:text-base ${styles.subtitle}`}>{subtitle}</p>
          )}
        </div>

        <div className="flex items-center justify-center gap-4">
          <GameIconButton
            icon="retry"
            tone={styles.retryTone}
            size="xl"
            label={playAgainLabel}
            onClick={onPlayAgain}
          />
          <GameIconButton
            icon="menu"
            tone={styles.menuTone}
            size="xl"
            label="Go back to Play menu"
            onClick={() => goTo("activities")}
          />
        </div>
      </div>
    </div>
  );
}
